import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  FileButton,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  Select,
  SelectProps,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  Timeline,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { format } from 'date-fns'
import {
  MailAttachment,
  Ticket,
  TicketPriority,
  TicketStatus,
  TicketUpdate,
} from '@data/tickets/model/ticket'
import {
  badgeRenderColors,
  IssueMap,
  MailTemplates,
  priorityColors,
  statusColors,
} from './constants'
import {
  AlertCircle,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  Droplets,
  Edit2,
  Mail,
  Maximize2,
  MessageCircle,
  Minimize2,
  Paperclip,
  RefreshCw,
  Send,
  Smartphone,
  Upload,
  User,
  Users,
  X,
} from 'lucide-react'
import { IconCheck } from '@tabler/icons-react'
import {
  getActionedByUsers,
  ticketHasHistory,
} from '@data/tickets/ticketActivity'
import { parseUtc } from '@common/utils/datetime/parseUtc'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { TemplatePreview } from './TemplatePreview'
import { CreatableMultiSelect } from './CreatableMultiSelect'
import { EmailEventDisplay } from './EmailEventDisplay'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from '@mantine/form'
import {
  useTicketById,
  useTicketUpdate,
} from '@data/tickets/tickets'
import { useRecipientByDeviceId } from '@data/mail/mail'
import { useDeviceById } from '@data/device/device'
import { useAuthStore } from '@store/auth.store'
import { DeviceModalMantine } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/DeviceModal'
import { SignalIcon } from '@components/ui/SignalIcon'
import UsageNavigator from '@components/main/LocationPerformance/components/UsageNavigator'
import { SimpleBattIcon } from '@components/ui/BattIcon'


interface TicketDetailsProps {
  ticket: Ticket;
  onClose: () => void;
  // onPriorityChange?: (ticketId: number, newPriority: TicketPriority) => void;
  // onStatusChange?: (ticketId: number, newStatus: TicketStatus) => void;
  // onDescriptionChange?: (ticketId: number, newDescription: string) => void;
}

export interface TicketFormData {
  device_name: string,
  device_id?: string,
  contact_name: string
  additional_info_pre: string,
  additional_info_post: string,
  sha256: string,
}

interface TicketDraftValues {
  priority: TicketPriority
  status: TicketStatus
  description: string | undefined
  actionComment: string
  toRecipients: string[]
  ccRecipients: string[]
  bccRecipients: string[]
  templateData: TicketFormData
}

const SYSTEM_USER_ID = 1899

const iconProps = {
  stroke: 1.5,
  color: 'currentColor',
  opacity: 0.6,
  size: 18,
}

const renderSelectOption: SelectProps['renderOption'] = ({ option, checked }) => (
  <Group flex={'1'} gap={'xs'}>
    <Badge
      color={badgeRenderColors[option.value]}
      variant={'light'}
    >
      {option.value}
    </Badge>
    {checked && <IconCheck style={{ marginInlineStart: 'auto' }} {...iconProps} />}
  </Group>
)

const getTimelineTimestamp = (validFrom: string, actionDate: string | null | undefined) => {
  const validFromDate = new Date(validFrom)
  return new Date(actionDate ? new Date(actionDate) > validFromDate ? actionDate : validFrom : validFrom)
}

const mapTicketToDraftValues = (ticket: Ticket): TicketDraftValues => ({
  priority: ticket.priority,
  status: ticket.status,
  description: ticket.description,
  actionComment: '',
  toRecipients: [],
  ccRecipients: [],
  bccRecipients: [],
  templateData: {
    device_name: ticket.deviceName || '',
    device_id: ticket.deviceId,
    contact_name: 'Team',
    additional_info_pre: '',
    additional_info_post: '',
    sha256: '',
  },
})

const twoColumnGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
}

interface TimelineEntry {
  timestamp: Date
  epoch: number
  status: TicketStatus
  user: string
  gid: string
  priority: TicketPriority
  userIdRef?: number | null
  createdByIdRef: number
  resolvedEvent: boolean
}

interface TimelineTitleMeta {
  label: string
  color?: string
  showResolvedIcon: boolean
}

const isResolvedEvent = (
  eventDate: string,
  additionalDetail?: Ticket['additionalDetail'],
) => {
  const newDataDate = additionalDetail?.newDataDate

  return Boolean(
    newDataDate &&
    parseUtc(newDataDate).getTime() > new Date(eventDate).getTime(),
  )
}

const isSystemTimelineEvent = (
  entry: Pick<TimelineEntry, 'userIdRef' | 'createdByIdRef'>,
) => {
  return (
    entry.userIdRef === SYSTEM_USER_ID ||
    (entry.createdByIdRef === SYSTEM_USER_ID && entry.userIdRef === null)
  )
}

const getTimelineTitleMeta = (
  entry: TimelineEntry,
  issueType: Ticket['issueType'],
): TimelineTitleMeta => {
  if (entry.resolvedEvent) {
    return {
      label: 'Resolved Event',
      color: 'green',
      showResolvedIcon: true,
    }
  }

  if (isSystemTimelineEvent(entry)) {
    return {
      label: `${IssueMap[issueType]} Event`,
      showResolvedIcon: false,
    }
  }

  return {
    label: entry.user,
    showResolvedIcon: false,
  }
}

interface TicketDetailsPanelProps {
  ticket: Ticket
  actionedUsers: string[]
  description: string | undefined
  descriptionChanged: boolean
  hasHistory: boolean
  isAdhocCommentMode: boolean
  isEditingDescription: boolean
  priority: TicketPriority
  priorityChanged: boolean
  status: TicketStatus
  statusChanged: boolean
  onAddAdhocComment: () => void
  onDescriptionChange: (value: string) => void
  onOpenDeviceModal: () => void
  onOpenUsageNavigator: () => void
  onPriorityChange: (value: TicketPriority) => void
  onStatusChange: (value: string | null) => void
  onToggleDescriptionEdit: () => void
}

const TicketDetailsPanel = ({
                              ticket,
                              actionedUsers,
                              description,
                              descriptionChanged,
                              hasHistory,
                              isAdhocCommentMode,
                              isEditingDescription,
                              priority,
                              priorityChanged,
                              status,
                              statusChanged,
                              onAddAdhocComment,
                              onDescriptionChange,
                              onOpenDeviceModal,
                              onOpenUsageNavigator,
                              onPriorityChange,
                              onStatusChange,
                              onToggleDescriptionEdit,
                            }: TicketDetailsPanelProps) => {
  return (
    <Tabs.Panel value={'details'} pt={'md'}>
      <Stack gap={'md'}>
        <Group
          justify={'space-between'}
          align={'flex-start'}
        >
          <div style={{ flex: 1 }}>
            <Text fw={600} mb={'xs'}>
              {`${ticket.deviceId} - ${ticket.deviceName}`}
            </Text>
            {isEditingDescription ? (
              <>
                <Textarea
                  value={description || `${IssueMap[ticket.issueType]} issue detected`}
                  onChange={(e) => onDescriptionChange(e.target.value)}
                  size={'sm'}
                  autosize
                  minRows={2}
                  maxRows={6}
                  placeholder={'Enter ticket description...'}
                  description={'Edit Ticket Description'}
                  autoFocus
                />
                {descriptionChanged && (
                  <Text
                    size={'sm'}
                    c={'orange'}
                    mt={4}
                  >
                    ⚠ Pending change - click Update Status to save
                  </Text>
                )}
              </>
            ) : (
              <Text size={'sm'} c={'dimmed'}>
                {description || `${IssueMap[ticket.issueType]} issue detected`}
              </Text>
            )}
          </div>

          <Group gap={'xs'}>
            <Tooltip label={'Edit description'}>
              <ActionIcon
                variant={'light'}
                color={'blue'}
                onClick={onToggleDescriptionEdit}
              >
                <Edit2 size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={'Add adhoc comment'}>
              <ActionIcon
                variant={'light'}
                color={'blue'}
                onClick={onAddAdhocComment}
                disabled={isAdhocCommentMode}
              >
                <MessageCircle size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        <Divider />

        <div style={twoColumnGridStyle}>
          <Stack gap={'md'}>
            <div>
              <Group gap={'xs'} mb={4}>
                <Tooltip label={'Set the priority of the ticket'}>
                  <AlertCircle size={16} color={'gray'} />
                </Tooltip>
                <Text size={'xs'} c={'dimmed'}>
                  Priority
                </Text>
              </Group>
              <Group gap={'xs'} align={'center'}>
                <Select
                  value={priority}
                  onChange={(value) => value ? onPriorityChange(value as TicketPriority) : null}
                  data={[
                    { value: 'normal', label: 'Normal' },
                    { value: 'high', label: 'High' },
                  ]}
                  size={'sm'}
                  style={{ flex: 1 }}
                  renderOption={renderSelectOption}
                />
              </Group>
              {priorityChanged && (
                <Text
                  size={'sm'}
                  c={'orange'}
                  mt={4}
                >
                  ⚠ Pending change - click Update Status to save
                </Text>
              )}
            </div>

            <div>
              <Group gap={'xs'} mb={4}>
                <Circle size={16} color={'gray'} />
                <Text size={'sm'} c={'dimmed'}>
                  Status
                </Text>
              </Group>
              <Group gap={'xs'} align={'center'}>
                <Select
                  value={status}
                  onChange={onStatusChange}
                  data={[
                    { value: 'open', label: 'Open', disabled: false },
                    { value: 'in_progress', label: 'In Progress' },
                    { value: 'pending_review', label: 'Pending Review' },
                    { value: 'closed', label: 'Closed' },
                  ]}
                  size={'sm'}
                  style={{ flex: 1 }}
                  renderOption={renderSelectOption}
                />
              </Group>
              {statusChanged && (
                <Text
                  size={'sm'}
                  c={'orange'}
                  mt={4}
                >
                  ⚠ Pending change - click Update Status to save
                </Text>
              )}
            </div>
            <Divider />
            <Group justify={'center'}>
              <Text size={'md'} c={'dimmed'}>Device Status
                at {format(new Date(ticket.validFrom), 'dd-MM-yyyy HH:mm')}</Text>
            </Group>
            <Group gap={'sm'}>
              <div>
                <Text size={'sm'} c={'dimmed'}>
                  Check In
                </Text>
                <Text
                  size={'md'}
                  fw={500}
                  mt={2}
                >
                  {ticket.additionalDetail.lastPing ? format(parseUtc(ticket.additionalDetail.lastPing), 'dd-MM-yyyy HH:mm') : 'No Ping Data'}
                </Text>
              </div>
            </Group>

            <Group gap={'sm'}>
              <div>
                <Text size='sm' c='dimmed'>
                  Battery
                </Text>
                <SimpleBattIcon
                  batteryLevel={ticket.additionalDetail.internalBattery}
                  deviceType={ticket.deviceType}
                />
              </div>
            </Group>

            <Group gap='sm'>
              <div>
                <Text size='sm' c='dimmed'>
                  CSQ
                </Text>
                <SignalIcon signalLevel={ticket.additionalDetail.csq} />
              </div>
            </Group>

          </Stack>

          <Stack gap='md'>
            <Group gap='xs'>
              <Smartphone size={16} color='gray' />
              <div>
                <Text size='sm' c='dimmed'>
                  Device
                </Text>
                <Text
                  size='sm'
                  fw={500}
                  c='blue'
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                  onClick={onOpenDeviceModal}
                >
                  ID: {ticket.deviceId}
                </Text>
              </div>
            </Group>

            <Group gap='xs'>
              <Calendar size={16} color='gray' />
              <div>
                <Text size='sm' c='dimmed'>
                  1st Event Detected
                </Text>
                <Text
                  size='sm'
                  fw={500}
                  mt={2}
                >
                  {format(new Date(ticket.issueStartDate), 'dd-MM-yyyy HH:mm')}
                </Text>
              </div>
            </Group>

            <Group gap='xs'>
              <Calendar size={16} color='gray' />
              <div>
                <Text size='sm' c='dimmed'>
                  Ticket Created
                </Text>
                <Text
                  size='sm'
                  fw={500}
                  mt={2}
                >
                  {format(new Date(ticket.createdAt), 'dd-MM-yyyy HH:mm')}
                </Text>
              </div>
            </Group>

            <Group gap='xs'>
              <Calendar
                size={16}
                color='gray'
              />
              <div>
                <Text size='sm' c='dimmed'>
                  Last Event Detected
                </Text>
                <Text
                  size={'sm'}
                  fw={500}
                  mt={2}
                >
                  {format(new Date(ticket.eventDate), 'dd-MM-yyyy HH:mm')}
                </Text>
              </div>
            </Group>

            <Group
              c='blue'
              gap='xs'
              style={{ cursor: 'pointer' }}
              onClick={onOpenUsageNavigator}
            >
              <Droplets size={16} color='blue' />
              <Text style={{textDecoration: 'underline'}}>
                View Usage
              </Text>
            </Group>
          </Stack>
        </div>

        <Divider />
        <div style={twoColumnGridStyle}>
          {ticket.userIdRef && (
            <>
              <Stack gap='md'>
                <Group gap='xs'>
                  <User size={16} color='gray' />
                  <div>
                    <Text size='sm' c='dimmed'>
                      Assigned To
                    </Text>
                    <Text
                      size='sm'
                      fw={500}
                      mt={2}
                    >
                      {actionedUsers.length ? actionedUsers.join(', ') : '-'}
                    </Text>
                  </div>
                </Group>
              </Stack>
              <Stack gap='md'>
                <Group gap='xs'>
                  <User size={16} color='gray' />
                  <div>
                    <Text size='sm' c='dimmed'>
                      Updated at
                    </Text>
                    <Text
                      size='sm'
                      fw={500}
                      mt={2}
                    >
                      {ticket.actionDate ? format(new Date(ticket.actionDate), 'dd-MM-yyyy HH:mm') : null}
                    </Text>
                  </div>
                </Group>
              </Stack>
            </>
          )}
        </div>

        {hasHistory && actionedUsers.length > 1 && (
          <>
            <Divider />
            <Group gap='xs'>
              <Users size={16} color='gray' />
              <div>
                <Text size='sm' c='dimmed'>
                  All Users Who Actioned This Ticket
                </Text>
                <Text
                  size='sm'
                  fw={500}
                  mt={2}
                >
                  {actionedUsers.join(', ')}
                </Text>
              </div>
            </Group>
          </>
        )}
      </Stack>
    </Tabs.Panel>
  )
}

interface TicketHistoryPanelProps {
  commentsByGid: Record<string, Ticket['comments']>
  expandedComments: Record<string, boolean>
  filteredTimelineData: TimelineEntry[]
  issueType: Ticket['issueType']
  showSystemEvents: boolean
  onShowSystemEventsChange: (checked: boolean) => void
  onToggleExpandedComments: (gid: string) => void
}

const TicketHistoryPanel = ({
                              commentsByGid,
                              expandedComments,
                              filteredTimelineData,
                              issueType,
                              showSystemEvents,
                              onShowSystemEventsChange,
                              onToggleExpandedComments,
                            }: TicketHistoryPanelProps) => {
  return (
    <Tabs.Panel value='history' pt='md'>
      <Stack gap='md'>
        <Group
          gap='xs'
          mb='sm'
          justify='space-between'
        >
          <Group gap='xs'>
            <Clock size={16} color='gray' />
            <Text size='sm' c='dimmed'>
              Status Timeline & Comments
            </Text>
          </Group>
          <Switch
            label={'Show system events'}
            checked={showSystemEvents}
            onChange={(e) => onShowSystemEventsChange(e.currentTarget.checked)}
            size={'xs'}
          />
        </Group>
        <div
          style={{
            maxHeight: '600px',
            overflowY: 'auto',
          }}
        >
          <Timeline
            active={filteredTimelineData.length}
            bulletSize={24}
            lineWidth={2}
          >
            {filteredTimelineData.map((entry) => {
              const entryComments = commentsByGid[entry.gid] || []
              const titleMeta = getTimelineTitleMeta(entry, issueType)

              return (
                <Timeline.Item
                  key={`${entry.gid}-${entry.epoch}`}
                  bullet={<Circle size={12} />}
                  title={
                    <Group gap='xs'>
                      {titleMeta.showResolvedIcon && (
                        <AlertCircle size={14} color='green' />
                      )}
                      <Text size='sm' fw={500} c={titleMeta.color}>
                        {titleMeta.label}
                      </Text>
                      <Badge
                        color={statusColors[entry.status]}
                        variant='light'
                        size='sm'
                      >
                        {entry.status}
                      </Badge>
                      <Badge
                        color={priorityColors[entry.priority]}
                        variant='light'
                      >
                        {entry.priority}
                      </Badge>
                    </Group>
                  }
                >
                  <Text
                    size='sm'
                    c='dimmed'
                    mt={4}
                    mb='sm'
                  >
                    {format(new Date(entry.timestamp), 'dd-MM-yyyy HH:mm:ss')}
                  </Text>

                  {entryComments.length > 0 && (
                    <Stack gap='xs' mt='md'>
                      {(expandedComments[entry.gid] ? entryComments : entryComments.slice(0, 2)).map((comment) => (
                        <Paper
                          key={comment.id}
                          p={'sm'}
                          withBorder
                          style={{ backgroundColor: '#f8f9fa' }}
                        >
                          <Group
                            justify='space-between'
                            mb='xs'
                          >
                            <Group gap='xs'>
                              <MessageCircle size={14} color='gray' />
                              <Text size='sm' fw={600}>
                                {comment.userName}
                              </Text>
                            </Group>
                            <Text size='sm' c='dimmed'>
                              {format(new Date(comment.date), 'dd-MM-yyyy HH:mm:ss')}
                            </Text>
                          </Group>
                          <Text
                            size='sm'
                            style={{ lineHeight: 1.5 }}
                          >
                            {comment.comment}
                          </Text>
                        </Paper>
                      ))}
                      {entryComments.length > 2 && (
                        <Button
                          variant='subtle'
                          size='xs'
                          onClick={() => onToggleExpandedComments(entry.gid)}
                          style={{ alignSelf: 'flex-start' }}
                        >
                          {expandedComments[entry.gid]
                            ? `Show less`
                            : `Show ${entryComments.length - 2} more comment${entryComments.length - 2 > 1 ? 's' : ''}`}
                        </Button>
                      )}
                    </Stack>
                  )}

                  <div style={{ marginLeft: '1.5rem' }}>
                    <EmailEventDisplay gid={entry.gid} />
                  </div>
                </Timeline.Item>
              )
            })}
          </Timeline>
        </div>
      </Stack>
    </Tabs.Panel>
  )
}

interface TicketActionsPanelProps {
  MAX_ATTACHMENT_SIZE: number
  attachments: MailAttachment[]
  bccEmailError: string
  bccRecipients: string[]
  ccEmailError: string
  ccRecipients: string[]
  formData: TicketFormData
  isTemplateLoading: boolean
  issueRecipients: string[]
  showAttachments: boolean
  showBcc: boolean
  showCc: boolean
  templateId: string | null
  templateSelectItems: Array<{ value: string; label: string }>
  ticket: Ticket
  toRecipients: string[]
  totalAttachmentSize: number
  wasCanceled: boolean
  onBccRecipientsChange: (value: string[]) => void
  onCancelTemplateRequest: () => void
  onCcRecipientsChange: (value: string[]) => void
  onFileSelect: (files: File[] | null) => void
  onFormDataChange: React.Dispatch<React.SetStateAction<TicketFormData>>
  onRefetchReady: (refetch: () => void) => void
  onRefreshTemplate: () => void
  onRemoveAttachment: (index: number) => void
  onTemplateIdChange: (value: string | null) => void
  onTemplateLoadingChange: (loading: boolean) => void
  onTemplateValidationChange: (isValid: boolean) => void
  onToRecipientsChange: React.Dispatch<React.SetStateAction<string[]>>
  onToggleAttachments: () => void
  onToggleBcc: () => void
  onToggleCc: () => void
  setWasCanceled: React.Dispatch<React.SetStateAction<boolean>>
  validateEmail: (email: string) => boolean
  formatFileSize: (bytes: number) => string
}

const TicketActionsPanel = ({
                              MAX_ATTACHMENT_SIZE,
                              attachments,
                              bccEmailError,
                              bccRecipients,
                              ccEmailError,
                              ccRecipients,
                              formData,
                              isTemplateLoading,
                              issueRecipients,
                              showAttachments,
                              showBcc,
                              showCc,
                              templateId,
                              templateSelectItems,
                              ticket,
                              toRecipients,
                              totalAttachmentSize,
                              wasCanceled,
                              onBccRecipientsChange,
                              onCancelTemplateRequest,
                              onCcRecipientsChange,
                              onFileSelect,
                              onFormDataChange,
                              onRefetchReady,
                              onRefreshTemplate,
                              onRemoveAttachment,
                              onTemplateIdChange,
                              onTemplateLoadingChange,
                              onTemplateValidationChange,
                              onToRecipientsChange,
                              onToggleAttachments,
                              onToggleBcc,
                              onToggleCc,
                              setWasCanceled,
                              validateEmail,
                              formatFileSize,
                            }: TicketActionsPanelProps) => {
  return (
    <Tabs.Panel value='actions' pt='md'>
      <Stack gap='md'>
        <div style={twoColumnGridStyle}>
          <Stack gap='md'>
            <div>
              <Group gap={'xs'} mb={4}>
                <Tooltip label={'Select an email template'}>
                  <AlertCircle size={16} color={'gray'} />
                </Tooltip>
                <Text size='sm' c='dimmed'>
                  Email Template
                </Text>
              </Group>
              <Group gap='xs' align='center'>
                <Select
                  value={templateId}
                  onChange={onTemplateIdChange}
                  data={templateSelectItems}
                  size={'sm'}
                  style={{ flex: 1 }}
                />
                {templateId && !isTemplateLoading && (
                  <Tooltip label={'Refresh template'}>
                    <ActionIcon
                      variant='subtle'
                      color='blue'
                      size='sm'
                      onClick={onRefreshTemplate}
                    >
                      <RefreshCw size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </div>
          </Stack>
        </div>

        {templateId && (
          <>
            <Box pos='relative'>
              <LoadingOverlay
                visible={isTemplateLoading}
                zIndex={1000}
                overlayProps={{ radius: 'sm', blur: 2 }}
                loaderProps={{ color: 'blue', type: 'bars' }}
              />
              <Paper p='md' withBorder>
                <Group gap='xs' mb='md'>
                  <Mail
                    size={16}
                    color={'gray'}
                  />
                  <Text size='sm' fw={600}>
                    Select Recipients
                  </Text>
                  {isTemplateLoading && (
                    <Badge
                      size='sm'
                      variant='light'
                      color='blue'
                    >
                      Loading template...
                    </Badge>
                  )}
                </Group>

                <Stack gap='md'>
                  <CreatableMultiSelect
                    label={'To (Recipients)'}
                    description='Select from contacts or add custom email addresses'
                    placeholder={'Select or type email address'}
                    data={issueRecipients}
                    value={toRecipients}
                    onChange={onToRecipientsChange}
                    size={'sm'}
                    required
                    disabled={isTemplateLoading}
                    validateItem={(email) => {
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                      return emailRegex.test(email)
                    }}
                    error={toRecipients.length === 0 ? undefined : toRecipients.some(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) ? 'Invalid email format' : undefined}
                  />

                  <Paper
                    p='sm'
                    withBorder
                    style={{ backgroundColor: '#fafafa' }}
                  >
                    <Group
                      gap='xs'
                      mb={showCc ? 'md' : 0}
                      style={{ cursor: 'pointer' }}
                      onClick={onToggleCc}
                    >
                      <ActionIcon
                        size='sm'
                        variant='subtle'
                        color='gray'
                      >
                        {showCc ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </ActionIcon>
                      <Mail size={14} color='gray' />
                      <Text size='sm' fw={500}>
                        CC Recipients
                      </Text>
                      <Text size='sm' c='dimmed'>
                        (Optional)
                      </Text>
                      {ccRecipients.length > 0 && (
                        <Badge
                          size='sm'
                          variant='light'
                          color='blue'
                        >
                          {ccRecipients.length}
                        </Badge>
                      )}
                    </Group>

                    {showCc && (
                      <CreatableMultiSelect
                        label='CC Email Addresses'
                        description='Add email addresses for CC. Type an email and press Enter to add.'
                        placeholder='Type email and press Enter'
                        value={ccRecipients}
                        onChange={onCcRecipientsChange}
                        error={ccEmailError}
                        size='sm'
                        validateItem={validateEmail}
                        disabled={isTemplateLoading}
                      />
                    )}
                  </Paper>

                  <Paper
                    p='sm'
                    withBorder
                    style={{ backgroundColor: '#fafafa' }}
                  >
                    <Group
                      gap='xs'
                      mb={showBcc ? 'md' : 0}
                      style={{ cursor: 'pointer' }}
                      onClick={onToggleBcc}
                    >
                      <ActionIcon
                        size='sm'
                        variant='subtle'
                        color='gray'
                      >
                        {showBcc ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </ActionIcon>
                      <Mail size={14} color='gray' />
                      <Text size='sm' fw={500}>
                        BCC Recipients
                      </Text>
                      <Text size='sm' c='dimmed'>
                        (Optional)
                      </Text>
                      {bccRecipients.length > 0 && (
                        <Badge
                          size='sm'
                          variant='light'
                          color='blue'
                        >
                          {bccRecipients.length}
                        </Badge>
                      )}
                    </Group>

                    {showBcc && (
                      <CreatableMultiSelect
                        label='BCC Email Addresses'
                        description='Add email addresses for BCC. Type an email and press Enter to add.'
                        placeholder='Type email and press Enter'
                        value={bccRecipients}
                        onChange={onBccRecipientsChange}
                        error={bccEmailError}
                        size='sm'
                        validateItem={validateEmail}
                        disabled={isTemplateLoading}
                      />
                    )}
                  </Paper>
                </Stack>
              </Paper>
            </Box>
            <TemplatePreview
              templateId={templateId}
              deviceName={ticket.deviceName}
              deviceId={ticket.deviceId}
              onValidationChange={onTemplateValidationChange}
              onLoadingChange={onTemplateLoadingChange}
              onCancel={onCancelTemplateRequest}
              setWasCanceled={setWasCanceled}
              wasCanceled={wasCanceled}
              onRefetchReady={onRefetchReady}
              formState={[formData, onFormDataChange]}
            />

            <Box pos={'relative'}>
              <LoadingOverlay
                visible={isTemplateLoading}
                zIndex={1000}
                overlayProps={{ radius: 'sm', blur: 2 }}
                loaderProps={{ color: 'blue', type: 'bars' }}
              />
              <Paper p='md' withBorder>
                <Group
                  gap='xs'
                  mb={showAttachments ? 'md' : 0}
                  justify={'space-between'}
                >
                  <Group
                    gap='xs'
                    style={{ cursor: 'pointer', flex: 1 }}
                    onClick={onToggleAttachments}
                  >
                    <ActionIcon
                      size='sm'
                      variant='subtle'
                      color='gray'
                    >
                      {showAttachments ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </ActionIcon>
                    <Paperclip
                      size={16}
                      color='gray'
                    />
                    <Text size='sm' fw={600}>
                      Email Attachments
                    </Text>
                    {attachments.length > 0 && (
                      <Badge
                        size='sm'
                        variant='light'
                        color='blue'
                      >
                        {attachments.length}
                      </Badge>
                    )}
                  </Group>
                  {showAttachments && (
                    <FileButton
                      onChange={onFileSelect}
                      accept='*/*'
                      multiple
                    >
                      {(props) => (
                        <Button
                          {...props}
                          size='xs'
                          variant='light'
                          leftSection={<Upload size={16} />}
                          disabled={isTemplateLoading || !templateId}
                        >
                          Add Files
                        </Button>
                      )}
                    </FileButton>
                  )}
                </Group>

                {showAttachments && (
                  <>
                    {attachments.length > 0 ? (
                      <Stack gap='xs'>
                        {attachments.map((attachment, index) => (
                          <Paper
                            key={index}
                            p='xs'
                            withBorder
                            style={{ backgroundColor: '#f8f9fa' }}
                          >
                            <Group justify='space-between'>
                              <Group gap='xs'>
                                <Paperclip size={14} color='gray' />
                                <div>
                                  <Text size='sm' fw={500}>
                                    {attachment.fileName}
                                  </Text>
                                  <Text size='sm' c='dimmed'>
                                    {attachment.mime}
                                  </Text>
                                </div>
                              </Group>
                              <ActionIcon
                                size='sm'
                                color='red'
                                variant='subtle'
                                onClick={() => onRemoveAttachment(index)}
                              >
                                <X size={16} />
                              </ActionIcon>
                            </Group>
                          </Paper>
                        ))}
                        <Group justify='space-between' mt='xs'>
                          <Text size='sm' c='dimmed'>
                            {attachments.length} file{attachments.length > 1 ? 's' : ''} attached
                          </Text>
                          <Badge
                            size='sm'
                            variant='light'
                            color={totalAttachmentSize > MAX_ATTACHMENT_SIZE * 0.8 ? 'orange' : 'blue'}
                          >
                            {formatFileSize(totalAttachmentSize)} / 25 MB
                          </Badge>
                        </Group>
                      </Stack>
                    ) : (
                      <Text
                        size='sm'
                        c='dimmed'
                        ta='center'
                        py='md'
                      >
                        No attachments added. Maximum total size: 25 MB
                      </Text>
                    )}
                  </>
                )}
              </Paper>
            </Box>
          </>
        )}
      </Stack>
    </Tabs.Panel>
  )
}

export const TicketDetails = ({
                                ticket,
                                onClose,
                              }: TicketDetailsProps) => {
  const actionedUsers = getActionedByUsers(ticket, [SYSTEM_USER_ID])
  const hasHistory = ticketHasHistory(ticket)

  // The timeline needs the full history. The list sends it today; once it stops (see
  // docs/ticket-list-payload-api-request.md), fetch it for the one open ticket instead.
  // GET /tickets/{dUuid}/{ticketId} already returns it. Nothing is requested while the list
  // still carries it.
  const { data: fullTicket } = useTicketById(ticket.dUuid, ticket.id, ticket.history === undefined)
  const timelineHistory = ticket.history ?? fullTicket?.history
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({})
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [ccEmailError, setCcEmailError] = useState<string>('')
  const [bccEmailError, setBccEmailError] = useState<string>('')

  const form = useForm<TicketDraftValues>({
    initialValues: mapTicketToDraftValues(ticket),
  })

  const { data: recipientData } = useRecipientByDeviceId(ticket.dUuid, templateId)

  const issueRecipients = useMemo(() => {
    return recipientData?.to.map((recipient) => recipient.email) ?? []
  }, [recipientData?.to])

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(true)

  // Template form validation state
  const [isTemplateFormValid, setIsTemplateFormValid] = useState(false)

  // Template loading state
  const [isTemplateLoading, setIsTemplateLoading] = useState(false)

  const [commentError, setCommentError] = useState(false)

  // Adhoc comment mode state
  const [isAdhocCommentMode, setIsAdhocCommentMode] = useState(false)
  const [activeTab, setActiveTab] = useState<string | null>('details')

  // Attachment state
  const [attachments, setAttachments] = useState<MailAttachment[]>([])
  const [totalAttachmentSize, setTotalAttachmentSize] = useState(0)
  const [showAttachments, setShowAttachments] = useState(false)
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024 // 25MB in bytes

  // Template refetch function ref
  const templateRefetchRef = useRef<(() => void) | null>(null)

  // Canceled state for template
  const [wasCanceled, setWasCanceled] = useState(false)

  // Device info modal state
  const [showDeviceModal, setShowDeviceModal] = useState(false)
  const [showUsageNavigator, setShowUsageNavigator] = useState(false)
  const [showSystemEvents, setShowSystemEvents] = useState(true)

  const permissions = useAuthStore((state) => state.permissions)
  const queryClient = useQueryClient()
  const { mutate: updateTicket, isPending } = useTicketUpdate()
  const {
    data: deviceData,
    isFetching: isFetchingDeviceData,
    isPending: isPendingDeviceData,
  } = useDeviceById(ticket.dUuid, showDeviceModal || showUsageNavigator, permissions)

  const onHideDeviceModal = () => {
    setShowDeviceModal(false)
  }

  const templateSelectItems = useMemo(() => {
    return Object.entries(MailTemplates[ticket.issueType])
      .filter(item => item[1].HType.includes(ticket.deviceType))
      .map((item) => {
        return { value: item[1].tID, label: `${item[0]} - ${item[1].desc}` }
      })
  }, [ticket.deviceType, ticket.issueType])

  // Determine if email can be sent
  const canSendEmail = templateId && isTemplateFormValid && form.values.toRecipients.length > 0 && !bccEmailError

  const priorityChanged = form.values.priority !== ticket.priority
  const statusChanged = form.values.status !== ticket.status
  const descriptionChanged = form.values.description !== ticket.description

  const hasAnyChanges = priorityChanged || statusChanged || descriptionChanged

  // Determine if any action is pending
  const hasAnyAction = canSendEmail || hasAnyChanges || isAdhocCommentMode

  // Validate comment length (minimum 10 characters)
  const isCommentValid = form.values.actionComment.trim().length >= 10

  const isButtonEnabled = hasAnyAction && isCommentValid && !isPending


  useEffect(() => {
    const nextValues = mapTicketToDraftValues(ticket)

    form.setInitialValues(nextValues)
    form.setValues(nextValues)
    form.resetDirty(nextValues)
    setExpandedComments({})
    setIsEditingDescription(false)
    setCommentError(false)
    setIsAdhocCommentMode(false)
    setActiveTab('details')
  }, [ticket])

  useEffect(() => {
    if (
      ticket.status === 'open' &&
      (form.values.actionComment.length >= 10 || ticket.priority !== form.values.priority || templateId !== null)
    ) {
      form.setFieldValue('status', 'in_progress')
    }

  }, [form.values.actionComment, form.values.priority, templateId, ticket.priority, ticket.status])

  // Exit adhoc comment mode when status, priority, or email template changes
  useEffect(() => {
    if (isAdhocCommentMode && (statusChanged || priorityChanged || templateId)) {
      setIsAdhocCommentMode(false)
    }
  }, [statusChanged, priorityChanged, templateId, isAdhocCommentMode])

  const handleStatusChange = (value: string | null) => {
    if (!value) return
    if (ticket.status !== 'open' && value === 'open') {
      notifications.show({
        title: 'Ticket Status Invalid',
        message: 'Ticket status cannot be changed from Closed/In Progress to Open',
        color: 'red',
        icon: <AlertCircle size={16} />,
        autoClose: 6000,
      })
      return
    }
    form.setFieldValue('status', value as TicketStatus)
  }

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email.trim())
  }

  const validateCcEmails = (value: string[]) => {
    const invalidEmails = value.filter(email => email && !validateEmail(email))

    if (invalidEmails.length > 0) {
      setCcEmailError(`Invalid email(s): ${invalidEmails.join(', ')}`)
      return false
    }

    setCcEmailError('')
    return true
  }

  const validateBccEmails = (value: string[]) => {
    const invalidEmails = value.filter(email => email && !validateEmail(email))

    if (invalidEmails.length > 0) {
      setBccEmailError(`Invalid email(s): ${invalidEmails.join(', ')}`)
      return false
    }

    setCcEmailError('')
    return true
  }

  const handleCcEmailsChange = (value: string[]) => {
    form.setFieldValue('ccRecipients', value)
    validateCcEmails(value)
  }

  const handleBccEmailsChange = (value: string[]) => {
    form.setFieldValue('bccRecipients', value)
    validateBccEmails(value)
  }

  const handleActionCommentChange = (value: string) => {
    form.setFieldValue('actionComment', value)
    if (value.trim().length >= 10) {
      setCommentError(false)
    }
  }

  const handleAdhocComment = () => {
    setIsAdhocCommentMode(true)
  }

  const handleExpandedCommentsToggle = (gid: string) => {
    setExpandedComments(prev => ({
      ...prev,
      [gid]: !prev[gid],
    }))
  }

  const setTemplateFormData = useCallback<React.Dispatch<React.SetStateAction<TicketFormData>>>((value) => {
    const currentValue = form.getValues().templateData
    const nextValue = typeof value === 'function'
      ? value(currentValue)
      : value

    if (currentValue === nextValue) {
      return
    }

    form.setFieldValue('templateData', nextValue)
  }, [form])

  const handleTemplateRefetchReady = useCallback((refetch: () => void) => {
    templateRefetchRef.current = refetch
  }, [])

  const handleRefreshTemplate = useCallback(() => {
    if (templateRefetchRef.current) {
      templateRefetchRef.current()
    }
  }, [])

  const handleToRecipientsChange = useCallback<React.Dispatch<React.SetStateAction<string[]>>>((value) => {
    const currentValue = form.getValues().toRecipients
    const nextValue = typeof value === 'function' ? value(currentValue) : value
    form.setFieldValue('toRecipients', nextValue)
  }, [form])

  // File attachment handlers
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Remove the data:mime;base64, prefix
          const base64 = reader.result.split(',')[1]
          resolve(base64)
        } else {
          reject(new Error('Failed to convert file to base64'))
        }
      }
      reader.onerror = (error) => reject(error)
    })
  }

  const handleFileSelect = async (files: File[] | null) => {
    if (!files || files.length === 0) return

    // Calculate current total size + new files size
    const newFilesSize = files.reduce((sum, file) => sum + file.size, 0)
    const projectedTotalSize = totalAttachmentSize + newFilesSize

    if (projectedTotalSize > MAX_ATTACHMENT_SIZE) {
      notifications.show({
        title: 'Attachment Size Limit Exceeded',
        message: `Total attachment size cannot exceed 25MB. Current: ${formatFileSize(totalAttachmentSize)}, Adding: ${formatFileSize(newFilesSize)}`,
        color: 'red',
        icon: <AlertCircle size={16} />,
        autoClose: 5000,
      })
      return
    }

    try {
      const newAttachments: MailAttachment[] = await Promise.all(
        files.map(async (file) => {
          const base64Data = await convertFileToBase64(file)
          return {
            data: base64Data,
            mime: file.type || 'application/octet-stream',
            fileName: file.name,
            disposition: 'attachment',
          }
        }),
      )

      setAttachments((prev) => [...prev, ...newAttachments])
      setTotalAttachmentSize(projectedTotalSize)

      notifications.show({
        title: 'Files Attached',
        message: `Successfully attached ${files.length} file(s)`,
        color: 'green',
        icon: <Check size={16} />,
        autoClose: 3000,
      })
    } catch (error) {
      notifications.show({
        title: 'Attachment Failed',
        message: 'Failed to attach one or more files. Please try again.',
        color: 'red',
        icon: <AlertCircle size={16} />,
        autoClose: 5000,
      })
    }
  }

  const handleRemoveAttachment = (index: number) => {
    const removedAttachment = attachments[index]
    // Calculate the size of the removed file
    const removedSize = Math.ceil((removedAttachment.data.length * 3) / 4) // Approximate original file size from base64

    setAttachments((prev) => prev.filter((_, i) => i !== index))
    setTotalAttachmentSize((prev) => Math.max(0, prev - removedSize))

    notifications.show({
      title: 'Attachment Removed',
      message: `Removed ${removedAttachment.fileName}`,
      color: 'blue',
      icon: <Check size={16} />,
      autoClose: 2000,
    })
  }

  const handleUpdateStatus = () => {
    // Validate comment is provided and meets minimum length
    if (form.values.actionComment.trim().length < 10) {
      setCommentError(true)
      notifications.show({
        title: 'Comment Required',
        message: 'Please provide a comment of at least 10 characters explaining the reason for this action',
        color: 'red',
        icon: <AlertCircle size={16} />,
        autoClose: 3000,
      })
      return
    }

    const updatePayload: TicketUpdate = {
      dUuid: ticket.dUuid,
      ticketId: ticket.id,
      status: form.values.status,
      priority: form.values.priority,
      description: form.values.description,
      comment: {
        department: 'monitoring',
        category: 'general',
        comment: form.values.actionComment,
        deleted: false,
      },
    }

    if (canSendEmail) {
      if (form.values.toRecipients.length === 0) {
        notifications.show({
          title: 'No Recipients Selected',
          message: 'Please select at least one recipient',
          color: 'red',
          icon: <AlertCircle size={16} />,
          autoClose: 3000,
        })
        return
      }

      if (form.values.ccRecipients.length > 0 && !validateCcEmails(form.values.ccRecipients)) {
        notifications.show({
          title: 'Invalid CC Emails',
          message: 'Please correct the CC email addresses',
          color: 'red',
          icon: <AlertCircle size={16} />,
          autoClose: 3000,
        })
        return
      }

      if (form.values.bccRecipients.length > 0 && !validateBccEmails(form.values.bccRecipients)) {
        notifications.show({
          title: 'Invalid BCC Emails',
          message: 'Please correct the BCC email addresses',
          color: 'red',
          icon: <AlertCircle size={16} />,
          autoClose: 3000,
        })
        return
      }

      updatePayload.mailContent = {
        templateId: templateId!,
        sha256: form.values.templateData.sha256,
        recipients: {
          to: form.values.toRecipients,
          cc: form.values.ccRecipients,
          bcc: form.values.bccRecipients,
        },
        attachments: attachments.length > 0 ? attachments : undefined,
        dynamicTemplateData: {
          contactName: form.values.templateData.contact_name,
          deviceName: form.values.templateData.device_name,
          deviceId: ticket.deviceId,
          ticketId: `#${String(ticket.id).padStart(6, '0')}`,
          additionalInfoPre: form.values.templateData.additional_info_pre,
          additionalInfoPost: form.values.templateData.additional_info_post,
        },
      }
    }

    const submittedCanSendEmail = Boolean(canSendEmail)
    const submittedComment = form.values.actionComment.trim()
    const submittedUpdates: string[] = []

    if (priorityChanged) {
      submittedUpdates.push(`Priority changed to ${form.values.priority}`)
    }
    if (statusChanged) {
      submittedUpdates.push(`Status changed to ${form.values.status}`)
    }
    if (descriptionChanged) {
      submittedUpdates.push('Description updated')
    }

    const successTitle = submittedCanSendEmail
      ? 'Email Sent & Ticket Updated'
      : submittedUpdates.length > 0
        ? 'Ticket Updated Successfully'
        : 'Comment Saved'

    const successMessage = submittedUpdates.length > 0
      ? `${submittedUpdates.join(' • ')} • Comment: '${submittedComment}'`
      : `Comment: '${submittedComment}'`

    updateTicket(updatePayload, {
      onSuccess: () => {
        form.setFieldValue('actionComment', '')
        setCommentError(false)
        setIsAdhocCommentMode(false)

        if (submittedCanSendEmail) {
          setTemplateId(null)
          form.setFieldValue('toRecipients', [])
          form.setFieldValue('ccRecipients', [])
          form.setFieldValue('bccRecipients', [])
          setCcEmailError('')
          setBccEmailError('')
          form.setFieldValue('templateData', {
            device_name: ticket.deviceName || '',
            device_id: ticket.deviceId,
            contact_name: 'Team',
            additional_info_pre: '',
            additional_info_post: '',
            sha256: '',
          })
          setIsTemplateFormValid(false)
          setIsTemplateLoading(false)
          setWasCanceled(false)
          setAttachments([])
          setTotalAttachmentSize(0)
        }

        notifications.show({
          title: successTitle,
          message: successMessage,
          color: 'green',
          icon: <Check size={16} />,
          autoClose: 5000,
        })
        onClose()
      },
      onError: () => {
        notifications.show({
          title: 'Update Failed',
          message: 'An error occurred while updating the ticket. Please try again.',
          color: 'red',
          icon: <AlertCircle size={16} />,
          autoClose: 5000,
        })
      },
    })
  }


  // Determine button text and label based on mode
  const buttonText = isAdhocCommentMode
    ? 'Save Comment'
    : canSendEmail
      ? 'Update Status/Send Email'
      : 'Update Status'
  const commentLabel = isAdhocCommentMode ? 'Adhoc Comment' : 'Action Comment'

  const timelineData = useMemo<TimelineEntry[]>(() => {
    const historyEntries = timelineHistory
      ? [...timelineHistory].reverse().map((entry) => {
        const itemDate = getTimelineTimestamp(entry.validFrom, entry.actionDate)

        return {
          timestamp: itemDate,
          epoch: itemDate.getTime(),
          status: entry.status,
          user: entry.userName ? entry.userName : entry.createdByUserName,
          gid: entry.gid,
          priority: entry.priority,
          userIdRef: entry.userIdRef,
          createdByIdRef: entry.createdByIdRef,
          resolvedEvent: isResolvedEvent(entry.eventDate, entry.additionalDetail),
        }
      })
      : []

    const currentTicketTimestamp = getTimelineTimestamp(ticket.validFrom, ticket.actionDate)

    historyEntries.unshift({
      timestamp: currentTicketTimestamp,
      epoch: currentTicketTimestamp.getTime(),
      status: ticket.status,
      user: ticket.userName ? ticket.userName : ticket.createdByUserName,
      gid: ticket.gid,
      priority: ticket.priority,
      userIdRef: ticket.userIdRef,
      createdByIdRef: ticket.createdByIdRef,
      resolvedEvent: isResolvedEvent(ticket.eventDate, ticket.additionalDetail),
    })

    historyEntries.sort((a, b) => b.epoch - a.epoch)
    return historyEntries
  }, [
    ticket.actionDate,
    ticket.additionalDetail,
    ticket.createdByIdRef,
    ticket.gid,
    timelineHistory,
    ticket.priority,
    ticket.status,
    ticket.eventDate,
    ticket.userIdRef,
    ticket.userName,
    ticket.createdByUserName,
    ticket.validFrom
  ])

  const filteredTimelineData = useMemo(() => {
    return showSystemEvents
      ? timelineData
      : timelineData.filter(entry => entry.userIdRef !== SYSTEM_USER_ID)
  }, [showSystemEvents, timelineData])

  const commentsByGid = useMemo<Record<string, Ticket['comments']>>(() => {
    const groupedComments: Record<string, typeof ticket.comments> = {}

    ticket.comments
      .filter(comment => !comment.deleted)
      .forEach(comment => {
        if (!groupedComments[comment.ticketGidRef]) {
          groupedComments[comment.ticketGidRef] = []
        }
        groupedComments[comment.ticketGidRef].push(comment)
      })

    Object.keys(groupedComments).forEach(gid => {
      groupedComments[gid].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    })

    return groupedComments
  }, [ticket.comments])

  const cancelTemplateRequest = () => {
    queryClient.invalidateQueries({ queryKey: ['mailTemplate', templateId] })
  }

  return (
    <Modal.Root
      opened={!!ticket}
      onClose={onClose}
      size={isFullscreen ? '100%' : '50%'}
      fullScreen={isFullscreen}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            <Group
              justify={'space-between'}
              style={{ width: '100%' }}
            >
              <Group>
                <Text fw={600}>
                  Ticket #{String(ticket.id).padStart(6, '0')}
                </Text>
                <Badge
                  color={priorityColors[ticket.priority]}
                  variant={'light'}
                >
                  {ticket.priority}
                </Badge>
                <Badge
                  color={statusColors[ticket.status]}
                  variant={'light'}
                >
                  {ticket.status}
                </Badge>
              </Group>
            </Group>
          </Modal.Title>
          <Group>
            <ActionIcon
              variant={'subtle'}
              c={'dimmed'}
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </ActionIcon>
            <Modal.CloseButton />
          </Group>
        </Modal.Header>
        <Modal.Body>
          <Box pos='relative'>
            <LoadingOverlay
              visible={isPending}
              zIndex={1000}
              overlayProps={{ radius: 'sm', blur: 2 }}
              loaderProps={{ color: 'blue', type: 'bars' }}
            />
            <Tabs
              value={activeTab}
              onChange={setActiveTab}
            >
              <Tabs.List>
                <Tabs.Tab value={'details'}>Details</Tabs.Tab>
                <Tabs.Tab value={'history'}>History</Tabs.Tab>
                <Tabs.Tab value={'actions'}>Contact Client</Tabs.Tab>
              </Tabs.List>

              <TicketDetailsPanel
                ticket={ticket}
                actionedUsers={actionedUsers}
                description={form.values.description}
                descriptionChanged={descriptionChanged}
                hasHistory={hasHistory}
                isAdhocCommentMode={isAdhocCommentMode}
                isEditingDescription={isEditingDescription}
                priority={form.values.priority}
                priorityChanged={priorityChanged}
                status={form.values.status}
                statusChanged={statusChanged}
                onAddAdhocComment={handleAdhocComment}
                onDescriptionChange={(value) => form.setFieldValue('description', value)}
                onOpenDeviceModal={() => setShowDeviceModal(true)}
                onOpenUsageNavigator={() => setShowUsageNavigator(true)}
                onPriorityChange={(value) => form.setFieldValue('priority', value)}
                onStatusChange={handleStatusChange}
                onToggleDescriptionEdit={() => setIsEditingDescription(prev => !prev)}
              />

              <TicketHistoryPanel
                commentsByGid={commentsByGid}
                expandedComments={expandedComments}
                filteredTimelineData={filteredTimelineData}
                issueType={ticket.issueType}
                showSystemEvents={showSystemEvents}
                onShowSystemEventsChange={setShowSystemEvents}
                onToggleExpandedComments={handleExpandedCommentsToggle}
              />

              <TicketActionsPanel
                MAX_ATTACHMENT_SIZE={MAX_ATTACHMENT_SIZE}
                attachments={attachments}
                bccEmailError={bccEmailError}
                bccRecipients={form.values.bccRecipients}
                ccEmailError={ccEmailError}
                ccRecipients={form.values.ccRecipients}
                formData={form.values.templateData}
                isTemplateLoading={isTemplateLoading}
                issueRecipients={issueRecipients}
                showAttachments={showAttachments}
                showBcc={showBcc}
                showCc={showCc}
                templateId={templateId}
                templateSelectItems={templateSelectItems}
                ticket={ticket}
                toRecipients={form.values.toRecipients}
                totalAttachmentSize={totalAttachmentSize}
                wasCanceled={wasCanceled}
                onBccRecipientsChange={handleBccEmailsChange}
                onCancelTemplateRequest={cancelTemplateRequest}
                onCcRecipientsChange={handleCcEmailsChange}
                onFileSelect={handleFileSelect}
                onFormDataChange={setTemplateFormData}
                onRefetchReady={handleTemplateRefetchReady}
                onRefreshTemplate={handleRefreshTemplate}
                onRemoveAttachment={handleRemoveAttachment}
                onTemplateIdChange={setTemplateId}
                onTemplateLoadingChange={setIsTemplateLoading}
                onTemplateValidationChange={setIsTemplateFormValid}
                onToRecipientsChange={handleToRecipientsChange}
                onToggleAttachments={() => setShowAttachments(prev => !prev)}
                onToggleBcc={() => setShowBcc(prev => !prev)}
                onToggleCc={() => setShowCc(prev => !prev)}
                setWasCanceled={setWasCanceled}
                validateEmail={validateEmail}
                formatFileSize={formatFileSize}
              />
            </Tabs>

            {/* Action Comment Field - Only show when changes detected */}
            {hasAnyAction && (
              <>
                <Divider mt='md' />
                <Paper
                  p='md'
                  withBorder
                  mt='md'
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <Textarea
                    label={commentLabel}
                    description={`Required: Minimum 10 characters. ${form.values.actionComment.trim().length}/10`}
                    placeholder='Enter a comment explaining the reason for this change...'
                    value={form.values.actionComment}
                    onChange={(e) => handleActionCommentChange(e.target.value)}
                    error={commentError && 'Comment must be at least 10 characters'}
                    required
                    autosize
                    minRows={2}
                    maxRows={4}
                    size={'sm'}
                  />
                </Paper>
              </>
            )}

            {/* Action buttons sticky footer */}
            <Divider mt='md' />
            <Group grow mt={'md'}>
              <Button
                variant='filled'
                // onClick={canSendEmail ? handleSendEmail : handleUpdateStatus}
                onClick={handleUpdateStatus}
                disabled={!isButtonEnabled}
                leftSection={canSendEmail ? <Send size={16} /> : undefined}
              >
                {buttonText}
              </Button>
              <Button
                variant='outline'
                onClick={onClose}
              >Cancel</Button>
            </Group>
          </Box>
        </Modal.Body>
      </Modal.Content>

      {showDeviceModal && !isFetchingDeviceData && !isPendingDeviceData && deviceData ?
        <DeviceModalMantine
          opened={showDeviceModal}
          device={deviceData}
          onClose={onHideDeviceModal}
        />
        : null}

      {showUsageNavigator && deviceData &&
        <UsageNavigator
          show={showUsageNavigator}
          deviceSelected={deviceData}
          onHide={() => setShowUsageNavigator(false)}
        />
      }
    </Modal.Root>
  )
}
